import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * @returns {Promise<number>}
   */
  fetchCartItemCount = async () => {
    // Avoid stale responses when restoring state (e.g. back from checkout).
    const response = await fetch('/cart.js', { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart fetch failed');
    const cart = await response.json();
    return typeof cart.item_count === 'number' ? cart.item_count : 0;
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event.
   * Always re-syncs the cart count — covers both bfcache restores (event.persisted === true)
   * AND full reloads triggered by the browser Back button after Shopify's hosted checkout
   * redirect (event.persisted === false), where the old cached count may be stale.
   * @param {PageTransitionEvent} _event - The page show event.
   */
  onPageShow = (_event) => {
    this.ensureCartBubbleIsCorrect();
  };

  /**
   * Handles the cart update event.
   * When the event carries a pre-fetched `totalCartCount` (set by product-form.js from
   * the /cart.js call it already makes), we use that directly so we don't need to make
   * a second network request just to update the bubble count.
   * @param {CustomEvent<{ data?: { didError?: boolean, totalCartCount?: number } }>} event
   */
  onCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    try {
      // Use the count already fetched by product-form.js when available.
      const preComputedCount = event.detail?.data?.totalCartCount;
      const itemCount =
        typeof preComputedCount === 'number'
          ? preComputedCount
          : await this.fetchCartItemCount();

      await this.renderCartBubble(itemCount, true);
    } catch {
      // Keep existing UI if cart cannot be read
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Total line-item quantity from the cart.
   * @param {boolean} [animate] - Whether to run the count animation.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.refs.cartBubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Re-syncs the bubble from the server (e.g. after bfcache restore).
   */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;

    try {
      const itemCount = await this.fetchCartItemCount();
      await this.renderCartBubble(itemCount, false);
    } catch {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
